import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import { useAuth } from '../context/AuthContext';
import storage from '../utils/storage';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { initializeApp, getApp } from 'firebase/app';
import { firebaseConfig } from '../../firebase.config';
import {
  School,
  AutoAwesome,
  Psychology,
  Analytics,
  Group,
  FitnessCenter,
  Favorite,
  Schedule,
  AccessTime,
  Bedtime,
  Science,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';

// Initialize Firebase if needed
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}
const db = getFirestore(app);
const auth = getAuth(app);

// Icon mapping
const iconMap = {
  'school': School,
  'auto-awesome': AutoAwesome,
  'psychology': Psychology,
  'analytics': Analytics,
  'group': Group,
  'fitness-center': FitnessCenter,
  'favorite': Favorite,
  'schedule': Schedule,
  'access-time': AccessTime,
  'bedtime': Bedtime,
  'science': Science,
};

const AppOnboardingScreen = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const { setOnboarded, syncUserDataFromFirestore } = useFlashcards();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);

  const appExplanationSteps = [
    {
      icon: 'school',
      title: t('onboarding.welcome'),
      subtitle: t('onboarding.subtitle'),
      description: t('onboarding.description'),
      color: theme.primary,
    },
    {
      icon: 'auto-awesome',
      title: t('onboarding.aiCreation'),
      subtitle: t('onboarding.aiSubtitle'),
      description: t('onboarding.aiDescription'),
      color: theme.accent,
    },
    {
      icon: 'psychology',
      title: t('onboarding.spacedRepetition'),
      subtitle: t('onboarding.spacedSubtitle'),
      description: t('onboarding.spacedDescription'),
      color: theme.primary,
    },
    {
      icon: 'analytics',
      title: t('onboarding.progressTracking'),
      subtitle: t('onboarding.progressSubtitle'),
      description: t('onboarding.progressDescription'),
      color: theme.success,
    },
    {
      icon: 'group',
      title: t('onboarding.organizeClasses'),
      subtitle: t('onboarding.organizeSubtitle'),
      description: t('onboarding.organizeDescription'),
      color: theme.accent,
    },
  ];

  const neuroscienceSteps = [
    {
      icon: 'psychology',
      title: t('onboarding.neuroscience.step1.title'),
      subtitle: t('onboarding.neuroscience.step1.subtitle'),
      description: t('onboarding.neuroscience.step1.description'),
      scienceFact: t('onboarding.neuroscience.step1.scienceFact'),
      color: theme.primary,
    },
    {
      icon: 'fitness-center',
      title: t('onboarding.neuroscience.step2.title'),
      subtitle: t('onboarding.neuroscience.step2.subtitle'),
      description: t('onboarding.neuroscience.step2.description'),
      scienceFact: t('onboarding.neuroscience.step2.scienceFact'),
      color: theme.accent,
    },
    {
      icon: 'favorite',
      title: t('onboarding.neuroscience.step3.title'),
      subtitle: t('onboarding.neuroscience.step3.subtitle'),
      description: t('onboarding.neuroscience.step3.description'),
      scienceFact: t('onboarding.neuroscience.step3.scienceFact'),
      color: theme.success,
    },
    {
      icon: 'schedule',
      title: t('onboarding.neuroscience.step4.title'),
      subtitle: t('onboarding.neuroscience.step4.subtitle'),
      description: t('onboarding.neuroscience.step4.description'),
      scienceFact: t('onboarding.neuroscience.step4.scienceFact'),
      color: theme.primary,
    },
    {
      icon: 'access-time',
      title: t('onboarding.neuroscience.step5.title'),
      subtitle: t('onboarding.neuroscience.step5.subtitle'),
      description: t('onboarding.neuroscience.step5.description'),
      scienceFact: t('onboarding.neuroscience.step5.scienceFact'),
      color: theme.accent,
    },
    {
      icon: 'bedtime',
      title: t('onboarding.neuroscience.step6.title'),
      subtitle: t('onboarding.neuroscience.step6.subtitle'),
      description: t('onboarding.neuroscience.step6.description'),
      scienceFact: t('onboarding.neuroscience.step6.scienceFact'),
      color: theme.accent,
    },
    {
      icon: 'science',
      title: t('onboarding.neuroscience.step7.title'),
      subtitle: t('onboarding.neuroscience.step7.subtitle'),
      description: t('onboarding.neuroscience.step7.description'),
      scienceFact: t('onboarding.neuroscience.step7.scienceFact'),
      color: theme.success,
    },
  ];

  const getCurrentSteps = () => {
    return currentSection === 0 ? appExplanationSteps : neuroscienceSteps;
  };

  const getTotalSteps = () => {
    return appExplanationSteps.length + neuroscienceSteps.length;
  };

  const getCurrentStepIndex = () => {
    return currentSection === 0 ? currentStep : appExplanationSteps.length + currentStep;
  };

  const handleNext = async () => {
    const currentSteps = getCurrentSteps();
    
    if (currentStep < currentSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentSection === 0) {
      setCurrentSection(1);
      setCurrentStep(0);
    } else {
      try {
        const currentUser = auth.currentUser;
        const userPrefix = currentUser ? `user_${currentUser.uid}_` : '';
        const user = JSON.parse(await storage.getItem(`${userPrefix}user`) || '{}');
        await storage.setItem(`${userPrefix}user`, JSON.stringify({ ...user, isOnboarded: true }));
        
        setOnboarded(true);
        
        if (authUser) {
          try {
            const userDocRef = doc(db, 'users', authUser.uid);
            await updateDoc(userDocRef, {
              isOnboarded: true,
            });
          } catch (firestoreError) {
            console.error('Error updating Firestore:', firestoreError);
          }
        }
        
        await syncUserDataFromFirestore();
      } catch (error) {
        console.error('Error saving onboarding status:', error);
      }
      navigation.navigate('Home');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else if (currentSection === 1) {
      setCurrentSection(0);
      setCurrentStep(appExplanationSteps.length - 1);
    }
  };

  const handleSkip = async () => {
    try {
      const currentUser = auth.currentUser;
      const userPrefix = currentUser ? `user_${currentUser.uid}_` : '';
      const user = JSON.parse(await storage.getItem(`${userPrefix}user`) || '{}');
      await storage.setItem(`${userPrefix}user`, JSON.stringify({ ...user, isOnboarded: true }));
      
      setOnboarded(true);
      
      if (authUser) {
        try {
          const userDocRef = doc(db, 'users', authUser.uid);
          await updateDoc(userDocRef, {
            isOnboarded: true,
          });
        } catch (firestoreError) {
          console.error('Error updating Firestore:', firestoreError);
        }
      }
      
      await syncUserDataFromFirestore();
    } catch (error) {
      console.error('Error saving onboarding status:', error);
    }
    navigation.navigate('Home');
  };

  const currentStepData = getCurrentSteps()[currentStep];
  const isLastStep = currentStep === getCurrentSteps().length - 1 && currentSection === 1;
  const isFirstStep = currentStep === 0 && currentSection === 0;
  const currentStepIndex = getCurrentStepIndex();
  const totalSteps = getTotalSteps();
  const IconComponent = iconMap[currentStepData.icon] || School;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text style={[styles.progressText, { color: theme.textSecondary }]}>
            {currentStepIndex + 1} / {totalSteps}
          </Text>
          <TouchableOpacity onPress={handleSkip}>
            <Text style={[styles.skipText, { color: theme.textSecondary }]}>
              {t('common.skip')}
            </Text>
          </TouchableOpacity>
        </View>
        <View style={[styles.progressBar, { backgroundColor: theme.border }]}>
          <View 
            style={[
              styles.progressFill, 
              { 
                backgroundColor: theme.primary,
                width: `${((currentStepIndex + 1) / totalSteps) * 100}%`
              }
            ]} 
          />
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.content}>
          <View style={[styles.iconCircle, { backgroundColor: currentStepData.color + '20' }]}>
            <IconComponent sx={{ fontSize: 48, color: currentStepData.color }} />
          </View>

          <Text style={[styles.title, { color: theme.text }]}>
            {currentStepData.title}
          </Text>

          <Text style={[styles.subtitle, { color: currentStepData.color }]}>
            {currentStepData.subtitle}
          </Text>

          <Text style={[styles.description, { color: theme.textSecondary }]}>
            {currentStepData.description}
          </Text>

          {currentSection === 1 && currentStepData.scienceFact && (
            <View style={[styles.scienceCard, { backgroundColor: theme.surface, borderColor: theme.border, borderLeftColor: currentStepData.color }]}>
              <Text style={[styles.scienceLabel, { color: currentStepData.color }]}>
                Science Fact
              </Text>
              <Text style={[styles.scienceText, { color: theme.text }]}>
                {currentStepData.scienceFact}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Footer */}
      <View style={[styles.footer, { borderTopColor: theme.border }]}>
        <View style={styles.buttonRow}>
          {!isFirstStep && (
            <TouchableOpacity 
              style={[styles.button, styles.previousButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
              onPress={handlePrevious}
            >
              <ArrowBack sx={{ fontSize: 20, color: theme.text }} />
              <Text style={[styles.buttonText, { color: theme.text }]}>
                {t('common.previous')}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[
              styles.button, 
              styles.nextButton,
              { 
                backgroundColor: isLastStep ? theme.success : theme.primary,
                flex: isFirstStep ? 1 : undefined,
              }
            ]}
            onPress={handleNext}
          >
            <Text style={[styles.buttonText, { color: 'white' }]}>
              {isLastStep ? t('onboarding.getStarted') : t('common.next')}
            </Text>
            {!isLastStep && (
              <ArrowForward sx={{ fontSize: 20, color: 'white' }} />
            )}
          </TouchableOpacity>
        </View>

        <View style={styles.dots}>
          {Array.from({ length: totalSteps }, (_, index) => (
            <View
              key={index}
              style={[
                styles.dot,
                {
                  backgroundColor: index <= currentStepIndex ? theme.primary : theme.border,
                  width: index === currentStepIndex ? 24 : 8,
                }
              ]}
            />
          ))}
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    paddingTop: 20,
    paddingHorizontal: 24,
    paddingBottom: 16,
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressText: {
    fontSize: 14,
    fontWeight: '600',
  },
  skipText: {
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
  },
  progressBar: {
    width: '100%',
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  scrollView: {
    flex: 1,
    marginBottom: 40,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 20,
    maxWidth: 600,
    width: '100%',
    margin: '0 auto',
    marginHorizontal: 24,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
    display: 'block',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 12,
    display: 'block',
    lineHeight: 1.3,
  },
  description: {
    fontSize: 16,
    lineHeight: 1.5,
    textAlign: 'center',
    marginBottom: 16,
    display: 'block',
    maxWidth: 500,
  },
  scienceCard: {
    marginTop: 4,
    padding: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderWidth: 1,
    width: '100%',
    maxWidth: 500,
  },
  scienceLabel: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
    display: 'block',
    lineHeight: 1.3,
  },
  scienceText: {
    fontSize: 14,
    lineHeight: 1.5,
    display: 'block',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 40,
    paddingBottom: 24,
    marginTop: 0,
    borderTopWidth: 1,
    maxWidth: 1200,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    gap: 12,
    maxWidth: 600,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  button: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 26,
    paddingHorizontal: 32,
    borderRadius: 12,
    gap: 8,
    cursor: 'pointer',
    minWidth: 160,
  },
  previousButton: {
    borderWidth: 1,
    minWidth: 120,
  },
  nextButton: {
    minWidth: 120,
    
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  dots: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  dot: {
    height: 8,
    borderRadius: 4,
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },
});

export default AppOnboardingScreen;
